import { Component, DestroyRef, inject, Injector, runInInjectionContext } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { Observable } from 'rxjs';
import { MainBootstrapComponent } from '@lentau/bootstrap/feature/components/main-bootstrap/main-bootstrap.component';
import { BrowserApiSupportService } from '@lentau/ui-kit/core/services/browser-api-support/browser-api-support.service';
import { EagerCommonBootstrapCSRService } from '@lentau/bootstrap/feature/services/eager-common-bootstrap.csr.service';
import { EagerLentaBootstrapCSRService } from '@lentau/bootstrap/feature/services/eager-lenta-bootstrap.csr.service';
import { getDomNewSiteRedirectStrategy } from '@lentau/city/application/dom-new-site-redirect/dom-new-site-redirect.factory';
import { DomNewSiteRedirectStrategy } from '@lentau/city/application/dom-new-site-redirect/dom-new-site-redirect.strategy';
import { AfterInteractCommonBootstrapCSRService } from '@lentau/bootstrap/feature/services/after-interact-common-bootstrap.csr.service';
import { LazyLoadAfterInteractCommonBootstrap } from '@lentau/bootstrap/feature/lazy-load-after-interact-common-bootstrap';
import { LazyLoadCommonBootstrap } from '@lentau/bootstrap/feature/lazy-load-common-bootstrap';
import { executeAfterSiteInteract } from '@lentau/ui-kit/core/helpers/site-interact.helper';
import { RenderTimeService } from '@lentau/core/measure';
import { LazyCommonBootstrapCSRService } from '@lentau/bootstrap/feature/services/lazy-common-bootstrap.csr.service';
import { LazyLoadLentaBootstrap } from '@lentau/bootstrap/feature/lazy-load-lenta-bootstrap';
import { LazyLentaBootstrapCSRService } from '@lentau/bootstrap/feature/services/lazy-lenta-bootstrap.csr.service';

@Component({
  imports: [RouterOutlet, MainBootstrapComponent],
  selector: 'dom-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  private eagerCommonBootstrapCSRService: EagerCommonBootstrapCSRService = inject(EagerCommonBootstrapCSRService);
  private eagerLentaBootstrapCSRService: EagerLentaBootstrapCSRService = inject(EagerLentaBootstrapCSRService);
  private readonly domNewSiteRedirectStrategy: DomNewSiteRedirectStrategy = getDomNewSiteRedirectStrategy();
  private browserApi: BrowserApiSupportService = inject(BrowserApiSupportService);
  private destroyRef: DestroyRef = inject(DestroyRef);
  private renderTimeService: RenderTimeService = inject(RenderTimeService);
  private injector: Injector = inject(Injector);

  private afterInteractCommonBootstrap$: Observable<AfterInteractCommonBootstrapCSRService> =
    LazyLoadAfterInteractCommonBootstrap();
  private lazyLoadCommonBootstrap$: Observable<LazyCommonBootstrapCSRService> = LazyLoadCommonBootstrap();
  private lazyLoadLentaBootstrap$: Observable<LazyLentaBootstrapCSRService> = LazyLoadLentaBootstrap();

  public isBrowser: boolean = this.browserApi.isBrowserAndNotSearchBot();

  constructor() {
    this.renderTimeService.init();

    this.bootstrapServices();
  }

  private bootstrapServices(): void {
    this.eagerCommonBootstrapCSRService.init();
    this.eagerLentaBootstrapCSRService.init();
    this.domNewSiteRedirectStrategy.init();

    this.lazyLoadCommonBootstrap$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((service: LazyCommonBootstrapCSRService) => {
        runInInjectionContext(this.injector, () => {
          service.init();
        });
      });

    this.lazyLoadLentaBootstrap$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((service: LazyLentaBootstrapCSRService) => {
        runInInjectionContext(this.injector, () => {
          service.init();
        });
      });

    executeAfterSiteInteract(() => {
      this.lazyLoadBootstrap();
    });
  }

  private lazyLoadBootstrap(): void {
    this.afterInteractCommonBootstrap$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((service: AfterInteractCommonBootstrapCSRService) => {
        runInInjectionContext(this.injector, () => {
          service.init();
        });
      });
  }
}
